import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Crown, Sparkles, Mic, BookOpen, Presentation, Headphones } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PERKS = [
  { icon: Sparkles, en: 'Unlimited AI Companion with deep insights', es: 'Compañero IA ilimitado con estudio profundo' },
  { icon: Mic, en: 'Sermon builder, PDF export & slideshows', es: 'Creador de sermones, PDF y diapositivas' },
  { icon: BookOpen, en: "Strong's word studies & commentaries", es: 'Estudios de Strong y comentarios' },
  { icon: Headphones, en: 'Premium AI voice narration', es: 'Narración con voz IA premium' },
  { icon: Presentation, en: 'Slidecon Pro included', es: 'Slidecon Pro incluido' },
];

/** One-time welcome card shown after sign-up while the free Lumera+ trial is active. */
const TrialWelcomeDialog = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const es = language === 'es';
  const [daysLeft, setDaysLeft] = useState(null);
  const [open, setOpen] = useState(false);
  const seenKey = user ? `lumera_trial_welcome_${user.user_id}` : null;

  useEffect(() => {
    if (!user || localStorage.getItem(seenKey)) return;
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/payments/subscription`, { withCredentials: true })
      .then(({ data }) => {
        const sub = data?.subscription;
        if (cancelled || !sub?.is_trial || !sub.expires_at) return;
        const ms = new Date(sub.expires_at).getTime() - Date.now();
        if (ms <= 0) return;
        setDaysLeft(Math.ceil(ms / 86400000));
        setOpen(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, seenKey]);

  const close = () => { localStorage.setItem(seenKey, '1'); setOpen(false); };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="trial-welcome-dialog">
        <div className="bg-gradient-to-br from-[#0F1A2E] to-[#2a2140] px-6 pt-8 pb-6 text-center text-white">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-400/20">
            <Crown className="h-6 w-6 text-amber-300" />
          </div>
          <DialogTitle className="text-2xl font-semibold">
            {es ? '¡Bienvenido(a) a Lumera+!' : 'Welcome to Lumera+!'}
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm text-slate-300" data-testid="trial-welcome-days">
            {es
              ? `Tu prueba gratis está activa: te quedan ${daysLeft} días. Sin tarjeta.`
              : `Your free trial is active — ${daysLeft} days left. No card needed.`}
          </DialogDescription>
        </div>
        <ul className="space-y-3 px-6 py-5">
          {PERKS.map(({ icon: Icon, en, es: esText }) => (
            <li key={en} className="flex items-center gap-3 text-sm">
              <Icon className="h-4 w-4 shrink-0 text-amber-600" />
              {es ? esText : en}
            </li>
          ))}
        </ul>
        <div className="flex flex-col gap-2 px-6 pb-6">
          <Button onClick={() => { close(); navigate('/companion'); }} className="rounded-full bg-amber-600 hover:bg-amber-700" data-testid="trial-welcome-start">
            {es ? 'Probar el Compañero IA' : 'Try the AI Companion'}
          </Button>
          <Button variant="ghost" onClick={close} data-testid="trial-welcome-close">
            {es ? 'Empezar a explorar' : 'Start exploring'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrialWelcomeDialog;
